import * as Sentry from '@sentry/nextjs'
import axios, { AxiosRequestConfig } from 'axios'
import type { AxiosInstance } from 'axios'
import { COMMUNITY_ACCESS_DENIED_EVENT } from 'shared/constants'
import { LockedError } from 'shared/errors/locked'
import { AccessDenied, AccessDeniedReasonsEnum } from '../errors/access-denied'
import { BadRequest } from '../errors/bad-request'
import { CommunityOwnerBlockedError } from '../errors/community-owner-blocked'
import { ConflictError } from '../errors/conflict-error'
import { InternalError } from '../errors/internal-error'
import { InvalidTokenError, invalidTokenErrorMessages } from '../errors/invalid-token-error'
import { MaintenanceError } from '../errors/maintenance-error'
import { NetworkError } from '../errors/network-error'
import { NotFound } from '../errors/not-found'
import { PayloadTooLarge } from '../errors/payload-too-large'
import { UnauthorizedResponse } from '../errors/unauthorized-response'
import { UnprocessableContentError } from '../errors/unprocessable-content-error'
import { regenerateJWT } from './regenerate-jwt'

export const createHttpClient = (
  baseURL?: string,
): {
  httpClient: AxiosInstance
  fetcher: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>
} => {
  const httpClient = axios.create({
    baseURL: baseURL,
    withCredentials: true,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })

  // TODO: move error handling to a separate function which can be unit tested
  httpClient.interceptors.response.use(
    response => response,
    error => {
      if (error.response) {
        switch (error.response.status) {
          case 400: {
            throw new BadRequest(error.response.data.errors, error.response)
          }
          case 401: {
            const redirectLocation = error.response.data?.redirect || error.response.data?.location
            if (invalidTokenErrorMessages.includes(error.response.data.message)) {
              throw new InvalidTokenError()
            } else if (redirectLocation) {
              throw new UnauthorizedResponse(redirectLocation)
            } else {
              Sentry.captureMessage('Unauthorized response', {
                level: 'error',
                extra: {
                  responseData: error.response.data,
                },
              })
            }

            break
          }
          case 403: {
            if (
              globalThis.window !== undefined &&
              globalThis.location.pathname.startsWith('/community')
            ) {
              const reason = error.response.data?.reason
              globalThis.dispatchEvent(
                new CustomEvent(COMMUNITY_ACCESS_DENIED_EVENT, { detail: { reason } }),
              )
              if (reason === AccessDeniedReasonsEnum.CommunityOwnerBlocked) {
                throw new CommunityOwnerBlockedError()
              }
            }
            throw new AccessDenied(error.response)
          }
          case 404: {
            throw new NotFound(error.response)
          }
          case 406: {
            throw new LockedError(error.response)
          }
          case 409: {
            throw new ConflictError(error.response.data)
          }
          case 413: {
            throw new PayloadTooLarge()
          }
          case 422: {
            throw new UnprocessableContentError(error.response)
          }
          case 500:
          case 502: {
            throw new InternalError()
          }
          case 503: {
            throw new MaintenanceError()
          }
          default: {
            if (error.code === 'ERR_NETWORK') {
              throw new NetworkError()
            }
          }
        }
      }
    },
  )

  let isJWTRegenerated = false

  const fetcher = <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    httpClient
      .get(url, config)
      .then(res => res.data)
      .catch(async error => {
        if (error instanceof UnauthorizedResponse) {
          globalThis.location.assign(error.redirect)
        } else if (error instanceof InvalidTokenError) {
          if (isJWTRegenerated) {
            Sentry.captureException(error, {
              extra: {
                context: 'Invalid token error',
              },
            })
          } else {
            try {
              // NOTE: Generating a new JWT
              await regenerateJWT()
              isJWTRegenerated = true
              // NOTE: repeat the same request
              return fetcher(url, config)
            } catch (error) {
              if (error instanceof UnauthorizedResponse) {
                globalThis.location.assign(error.redirect)
              }
            }
          }
        } else {
          throw error
        }
      })

  return { httpClient, fetcher }
}

export const { httpClient } = createHttpClient()
